import {
  type TruncationMode,
  _getTextWidth as getTextWidth,
} from '@dynatrace/strato-components/typography';

import { ITEM_FONT_STYLES, TEXT_ELLIPSIS } from '../constants.js';
import { getHorizontalLabelPosition } from './get-horizontal-label-position.js';
import { TRUNCATE_LABEL } from '../../../../utils/text-truncation.js';
import {
  HorizontalCoords,
  RangeLabel,
} from '../types/range-legend.internal.js';

/**
 * Calculates the starting position for a given label, in order to let the label
 * fitting the container bounds.
 */
export const calculateOptimalPlacement = (
  labelStart: number,
  labelEnd: number,
  startBound: number,
  endBound: number,
) => {
  if (labelStart < startBound) {
    const gap = startBound - labelStart;

    return labelStart + gap;
  }
  if (labelEnd > endBound) {
    const gap = endBound - labelEnd;

    return labelStart + gap;
  }

  return labelStart;
};

/**
 * Applies dynamically the ellipsis to the text until the label fit the
 * container bounds.
 */
export const calculateOptimalEllipsis = (
  text: string,
  startPosition: number,
  endPosition: number,
  truncationMode: TruncationMode,
  startBound: number,
  endBound: number,
): Required<Pick<RangeLabel, 'x'> & Pick<RangeLabel, 'text'>> => {
  let candidateStartPosX = startPosition;
  let candidateEndPosX = endPosition;
  let candidateLabel = text;
  let iteration = 0;

  do {
    candidateLabel = truncateLabel(candidateLabel, truncationMode);
    const newLabelWidth = getTextWidth(candidateLabel, ITEM_FONT_STYLES);
    const { x: newCandidateStartPosX } = getHorizontalLabelPosition(
      newLabelWidth,
      startBound,
      startBound + newLabelWidth,
      'start',
    );

    candidateStartPosX = newCandidateStartPosX;
    candidateEndPosX = candidateStartPosX + newLabelWidth;
    iteration++;

    if (iteration > text.length) {
      break;
    }
  } while (candidateStartPosX < startBound || candidateEndPosX > endBound);

  return { text: candidateLabel, x: candidateStartPosX };
};

export const truncateLabel = (text: string, truncationMode: TruncationMode) => {
  const filteredText = (text ?? '').replace(TEXT_ELLIPSIS, '');

  if (filteredText.length < 2) {
    return text;
  }

  return TRUNCATE_LABEL[truncationMode](filteredText);
};

/**
 * Returns the start and ending distribution of a label.
 * @param startLabel -
 * @param endLabel -
 * @param bounds -
 */
export const getLabelsPosition = (
  startLabel: string,
  endLabel: string,
  bounds: [number, number],
): { startLabel: HorizontalCoords; endLabel: HorizontalCoords } => {
  const startLabelWidth = getTextWidth(startLabel, ITEM_FONT_STYLES);
  const endLabelWidth = getTextWidth(endLabel, ITEM_FONT_STYLES);

  return {
    startLabel: { start: bounds[0], end: bounds[0] + startLabelWidth },
    endLabel: { start: bounds[1] - endLabelWidth, end: bounds[1] },
  };
};
